/**
 * Customers (Contacts) — list + create/edit. Data lives in CRM; this screen
 * talks to Billing's /contacts pass-through (Billing → Backbone → CRM), so the
 * app works standalone without a separate CRM UI. Create/edit gated to
 * ADMIN/EDITOR; VIEWER is read-only. Premium: gradient avatars, hover rows,
 * titled card, and a modal built from the shared input components.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, Text, TextInput, View } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useResponsive, MAX_CONTENT_WIDTH } from '../utils/responsive';
import { useSession } from '../context/sessionContext';
import SkIcon from '../components/skIcon';
import CommonInput from '../components/commonInput';
import EmailComponent from '../components/emailInput';
import MobileNumberInput, { COUNTRIES, CountryEntry } from '../components/mobileNumberInput';
import DropdownSelect from '../components/dropdownSelect';
import { initials } from '../utils/format';
import { REGEX } from '../utils/regex';
import { showToast } from '../components/skToast';
import ModalShell from '../components/modals/modalShell';
import ConfirmDialog from '../components/confirmDialog';
import SuccessDialog from '../components/successDialog';
import { listContacts, getContact, createContact, updateContact, deleteContact, changeContactPhone } from '../services/billing/contact.service';
import { listStates } from '../services/billing/accountant.service';
import { Contact, ContactType, CreateContactPayload } from '../schema/billing/contact';

const isWeb = Platform.OS === 'web';
const web = (s: Record<string, unknown>): any => (isWeb ? s : {});
const TYPES: ContactType[] = ['CUSTOMER', 'VENDOR', 'BOTH'];

const AV: Array<[string, string]> = [
  ['#f97316', '#db2777'], ['#3b82f6', '#6366f1'], ['#10b981', '#059669'],
  ['#f59e0b', '#ea580c'], ['#8b5cf6', '#6d28d9'], ['#06b6d4', '#0891b2'],
];
const avatarOf = (name: string): [string, string] => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h + name.charCodeAt(i)) % AV.length;
  return AV[h];
};

const fmtDate = (s: string | null): string => {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** Hover-reactive customer row with a gradient avatar + edit/delete actions. */
const ContactRow: React.FC<{ c: Contact; isMobile: boolean; W: Record<string, number>; stateName: string; onView: () => void; onEdit: () => void; onDelete: () => void }> = ({ c, isMobile, W, stateName, onView, onEdit, onDelete }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [hover, setHover] = useState(false);
  const [editHover, setEditHover] = useState(false);
  const [delHover, setDelHover] = useState(false);
  const [g1, g2] = avatarOf(c.name || '?');
  const tone = c.type === 'VENDOR'
    ? { soft: colors.neutralSoft, ink: colors.neutral, line: colors.neutralLine }
    : c.type === 'BOTH'
      ? { soft: colors.purpleSoft, ink: colors.purple, line: colors.purpleLine }
      : { soft: colors.accentSoft, ink: colors.accentInk, line: colors.accentLine };
  return (
    <Pressable
      onPress={onView}
      style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: colors.line, alignItems: 'center', backgroundColor: hover ? colors.paper2 : colors.card, ...web({ transition: 'background-color .12s ease', cursor: 'pointer' }) }}
      {...(isWeb ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
    >
      <View style={{ width: isMobile ? W.name : undefined, flex: isMobile ? undefined : 1.4, paddingHorizontal: 16, paddingVertical: 12 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 11 }}>
          <View style={{ width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center', backgroundColor: g2, ...web({ backgroundImage: `linear-gradient(135deg, ${g1}, ${g2})`, boxShadow: `0 2px 6px -2px ${g2}66` }) }}>
            <Text style={{ fontSize: 10, fontWeight: '700', color: '#fff', fontFamily: font.sansBold }}>{initials(c.name)}</Text>
          </View>
          <View style={{ minWidth: 0, flex: 1 }}>
            <Text numberOfLines={1} style={{ fontFamily: font.sansMedium, fontWeight: '500', fontSize: 13, color: colors.ink }}>{c.name}</Text>
            {!!c.company && <Text numberOfLines={1} style={{ fontFamily: font.sans, fontSize: 11, color: colors.ink4 }}>{c.company}</Text>}
          </View>
        </View>
      </View>
      <View style={{ width: W.email, paddingHorizontal: 16, paddingVertical: 12, justifyContent: 'center' }}><Text numberOfLines={1} style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink2 }}>{c.email || '—'}</Text></View>
      <View style={{ width: W.phone, paddingHorizontal: 16, paddingVertical: 12, justifyContent: 'center' }}><Text style={{ fontFamily: font.mono, fontSize: 12, color: colors.ink2 }}>{c.phone || '—'}</Text></View>
      <View style={{ width: W.gstin, paddingHorizontal: 16, paddingVertical: 12, justifyContent: 'center' }}><Text numberOfLines={1} style={{ fontFamily: font.mono, fontSize: 12, color: c.gstin ? colors.ink2 : colors.ink4 }}>{c.gstin || '—'}</Text></View>
      <View style={{ width: W.state, paddingHorizontal: 16, paddingVertical: 12, justifyContent: 'center' }}>
        {c.state_code ? (
          <>
            <Text numberOfLines={1} style={{ fontFamily: font.sansMedium, fontSize: 12, color: colors.ink2 }}>{stateName || c.state_code}</Text>
            <Text style={{ fontFamily: font.mono, fontSize: 10.5, color: colors.ink4 }}>Code {c.state_code}</Text>
          </>
        ) : <Text style={{ fontFamily: font.mono, fontSize: 12, color: colors.ink4 }}>—</Text>}
      </View>
      <View style={{ width: W.created, paddingHorizontal: 16, paddingVertical: 12, justifyContent: 'center' }}><Text style={{ fontFamily: font.mono, fontSize: 12, color: colors.ink3 }}>{fmtDate(c.created_at)}</Text></View>
      <View style={{ width: W.type, paddingHorizontal: 16, paddingVertical: 12, justifyContent: 'center', alignItems: 'flex-start' }}>
        <View style={{ paddingHorizontal: 9, paddingVertical: 3, borderRadius: 999, backgroundColor: tone.soft, borderWidth: 1, borderColor: tone.line }}>
          <Text style={{ fontFamily: font.sansSemibold, fontSize: 9.5, fontWeight: '600', letterSpacing: 0.3, color: tone.ink }}>{c.type}</Text>
        </View>
      </View>
      <View style={{ width: W.action, paddingHorizontal: 12, paddingVertical: 12, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
        <Pressable
          onPress={(e: any) => { e?.stopPropagation?.(); onEdit(); }}
          hitSlop={6}
          style={{ width: 30, height: 30, borderRadius: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: editHover ? colors.accentSoft : 'transparent', ...web({ transition: 'background-color .12s ease', cursor: 'pointer' }) }}
          {...(isWeb ? { onMouseEnter: () => setEditHover(true), onMouseLeave: () => setEditHover(false) } : {})}
        >
          <SkIcon name="edit" size={15} color={editHover ? colors.accentInk : colors.ink4} strokeWidth={2} />
        </Pressable>
        <Pressable
          onPress={(e: any) => { e?.stopPropagation?.(); onDelete(); }}
          hitSlop={6}
          style={{ width: 30, height: 30, borderRadius: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: delHover ? colors.dangerSoft : 'transparent', ...web({ transition: 'background-color .12s ease', cursor: 'pointer' }) }}
          {...(isWeb ? { onMouseEnter: () => setDelHover(true), onMouseLeave: () => setDelHover(false) } : {})}
        >
          <SkIcon name="trash" size={15} color={delHover ? colors.danger : colors.ink4} strokeWidth={2} />
        </Pressable>
      </View>
    </Pressable>
  );
};

const Contacts: React.FC = () => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const { isWide, isMobile } = useResponsive();
  const { role } = useSession();
  const canEdit = role !== 'VIEWER';

  const [rows, setRows] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [viewing, setViewing] = useState<Contact | null>(null);
  const [newHover, setNewHover] = useState(false);
  const [stateNames, setStateNames] = useState<Record<string, string>>({});
  const [toDelete, setToDelete] = useState<Contact | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deletedOpen, setDeletedOpen] = useState(false);

  // State-code → name map (for the State column).
  useEffect(() => {
    let alive = true;
    listStates()
      .then((list) => { if (alive) setStateNames(Object.fromEntries((list || []).map((s) => [s.code, s.name]))); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    listContacts()
      .then((list) => setRows(Array.isArray(list) ? list : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listContacts()
      .then((list) => { if (alive) setRows(Array.isArray(list) ? list : []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const shown = useMemo(() => {
    const active = rows.filter((c) => c.is_active !== false); // hide soft-deleted
    const q = query.trim().toLowerCase();
    if (!q) return active;
    return active.filter((c) =>
      (c.name + ' ' + (c.email || '') + ' ' + (c.phone || '') + ' ' + (c.gstin || '')).toLowerCase().includes(q),
    );
  }, [rows, query]);

  const doDelete = async () => {
    if (!toDelete || deleting) return;
    setDeleting(true);
    try {
      await deleteContact(toDelete.id);
      setToDelete(null);
      setDeleting(false);
      setDeletedOpen(true);
      load();
    } catch {
      setDeleting(false);
      setToDelete(null);
    }
  };

  const openNew = () => {
    if (!canEdit) { showToast('warn', 'Read-only', 'Switch to ADMIN/EDITOR to add customers'); return; }
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (c: Contact) => { setViewing(null); setEditing(c); setModalOpen(true); };
  const openView = (c: Contact) => setViewing(c);
  const onSaved = () => { setModalOpen(false); load(); };

  const card = { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14, ...theme.shadow('sm') } as const;
  const th = { fontFamily: font.sansSemibold, fontSize: 10.5, fontWeight: '600' as const, color: colors.ink4, letterSpacing: 0.6, textTransform: 'uppercase' as const };
  const W = { name: 240, email: 190, phone: 140, gstin: 150, state: 150, created: 120, type: 90, action: 92 };
  const tableMin = Object.values(W).reduce((a, b) => a + b, 0);

  return (
    <View style={{ maxWidth: MAX_CONTENT_WIDTH, width: '100%', alignSelf: 'center', paddingHorizontal: isWide ? 28 : 16 }}>
      {/* Filter bar */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <View style={{ width: 300, maxWidth: '55%', position: 'relative' }}>
          <View style={{ position: 'absolute', left: 12, top: 0, bottom: 0, justifyContent: 'center', zIndex: 1 }}>
            <SkIcon name="search" size={14} color={colors.ink4} strokeWidth={2} />
          </View>
          <TextInput
            value={query}
            onChangeText={setQuery}
            placeholder="Search customers…"
            placeholderTextColor={colors.ink4}
            style={{ height: 38, paddingLeft: 36, paddingRight: 12, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.card, borderRadius: 10, fontSize: 13, color: colors.ink, fontFamily: font.sans, ...theme.shadow('sm'), ...web({ outlineStyle: 'none' }) }}
          />
        </View>
        <View style={{ flex: 1 }} />
        <Pressable
          onPress={openNew}
          style={{ height: 38, paddingHorizontal: 16, borderRadius: 10, backgroundColor: colors.accent, flexDirection: 'row', alignItems: 'center', gap: 7, ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accent}, ${colors.accentInk})`, boxShadow: newHover ? `0 8px 18px -4px ${colors.accent}99` : `0 4px 10px -3px ${colors.accent}66`, transform: newHover ? 'translateY(-1px)' : 'translateY(0)', transition: 'transform .14s ease, box-shadow .14s ease', cursor: 'pointer' }) }}
          {...(isWeb ? { onMouseEnter: () => setNewHover(true), onMouseLeave: () => setNewHover(false) } : {})}
        >
          <SkIcon name="plus" size={15} color="#fff" strokeWidth={2.4} />
          <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: '#fff' }}>New customer</Text>
        </Pressable>
      </View>

      {/* Table */}
      <View style={[card, { overflow: 'hidden' }]}>
        {/* card header */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 14, paddingHorizontal: 16, borderBottomWidth: 1, borderColor: colors.line }}>
          <View style={{ width: 30, height: 30, borderRadius: 9, backgroundColor: colors.accentSoft, alignItems: 'center', justifyContent: 'center', ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})` }) }}>
            <SkIcon name="building" size={16} color={colors.accentInk} strokeWidth={2} />
          </View>
          <Text style={{ fontFamily: font.sansBold, fontSize: 14, fontWeight: '700', color: colors.ink, letterSpacing: -0.2 }}>Customers</Text>
          <View style={{ flex: 1 }} />
          {!loading && (
            <View style={{ paddingHorizontal: 9, height: 22, borderRadius: 999, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ fontFamily: font.monoMedium, fontSize: 11, color: colors.ink3 }}>{shown.length}</Text>
            </View>
          )}
        </View>
        <ScrollView horizontal={isMobile} showsHorizontalScrollIndicator={false}>
          <View style={{ width: isMobile ? tableMin : '100%' }}>
            <View style={{ flexDirection: 'row', backgroundColor: colors.paper2, borderBottomWidth: 1, borderColor: colors.line, ...web({ backgroundImage: `linear-gradient(180deg, ${colors.paper2}, ${colors.paper})` }) }}>
              <View style={{ width: isMobile ? W.name : undefined, flex: isMobile ? undefined : 1.4, paddingHorizontal: 16, paddingVertical: 10 }}><Text style={th}>Name</Text></View>
              <View style={{ width: W.email, paddingHorizontal: 16, paddingVertical: 10 }}><Text style={th}>Email</Text></View>
              <View style={{ width: W.phone, paddingHorizontal: 16, paddingVertical: 10 }}><Text style={th}>Phone</Text></View>
              <View style={{ width: W.gstin, paddingHorizontal: 16, paddingVertical: 10 }}><Text style={th}>GSTIN</Text></View>
              <View style={{ width: W.state, paddingHorizontal: 16, paddingVertical: 10 }}><Text style={th}>State</Text></View>
              <View style={{ width: W.created, paddingHorizontal: 16, paddingVertical: 10 }}><Text style={th}>Created</Text></View>
              <View style={{ width: W.type, paddingHorizontal: 16, paddingVertical: 10 }}><Text style={th}>Type</Text></View>
              <View style={{ width: W.action, paddingHorizontal: 12, paddingVertical: 10, alignItems: 'center' }}><Text style={th}>Action</Text></View>
            </View>

            {loading ? (
              <View style={{ padding: 48, alignItems: 'center' }}><Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink3 }}>Loading…</Text></View>
            ) : shown.length === 0 ? (
              <View style={{ paddingVertical: 56, paddingHorizontal: 24, alignItems: 'center' }}>
                <View style={{ width: 58, height: 58, borderRadius: 17, backgroundColor: colors.accentSoft, borderWidth: 1, borderColor: colors.accentLine, alignItems: 'center', justifyContent: 'center', marginBottom: 16, ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})`, boxShadow: `0 6px 16px -6px ${colors.accent}55` }) }}>
                  <SkIcon name="building" size={24} color={colors.accentInk} strokeWidth={2} />
                </View>
                <Text style={{ fontFamily: font.sansBold, fontWeight: '700', fontSize: 16, color: colors.ink, letterSpacing: -0.2 }}>{query ? 'No matches found' : 'No customers yet'}</Text>
                <Text style={{ fontFamily: font.sans, fontSize: 13, color: colors.ink3, marginTop: 5, textAlign: 'center' }}>{query ? 'Try a different search term.' : 'Add your first customer to start billing.'}</Text>
              </View>
            ) : (
              shown.map((c) => <ContactRow key={c.id} c={c} isMobile={isMobile} W={W} stateName={c.state_code ? stateNames[c.state_code] || '' : ''} onView={() => openView(c)} onEdit={() => openEdit(c)} onDelete={() => setToDelete(c)} />)
            )}
          </View>
        </ScrollView>
      </View>

      <ContactDetailModal
        visible={viewing != null}
        contact={viewing}
        stateName={viewing?.state_code ? stateNames[viewing.state_code] || '' : ''}
        canEdit={canEdit}
        onClose={() => setViewing(null)}
        onEdit={() => { if (viewing) openEdit(viewing); }}
        onDelete={() => { const c = viewing; setViewing(null); if (c) setToDelete(c); }}
      />

      <ContactModal visible={modalOpen} editing={editing} onClose={() => setModalOpen(false)} onSaved={onSaved} />

      {/* Delete (soft) — confirm → success */}
      <ConfirmDialog
        visible={toDelete != null}
        title="Delete this customer?"
        message={toDelete ? `“${toDelete.name}” will be removed from your customer list. This is reversible — the record is archived in CRM, not permanently erased.` : ''}
        confirmText="Delete customer"
        cancelText="Keep it"
        busy={deleting}
        onConfirm={doDelete}
        onCancel={() => (deleting ? null : setToDelete(null))}
      />
      <SuccessDialog
        visible={deletedOpen}
        title="Customer deleted"
        message="The customer has been archived and removed from your list."
        primaryAction={{ label: 'Done', onPress: () => setDeletedOpen(false) }}
        onClose={() => setDeletedOpen(false)}
      />
    </View>
  );
};

/* ── Read-only detail card ───────────────────────────────────────── */
const TYPE_TONE = (t: ContactType, colors: any) =>
  t === 'VENDOR' ? { soft: colors.neutralSoft, ink: colors.neutral, line: colors.neutralLine }
    : t === 'BOTH' ? { soft: colors.purpleSoft, ink: colors.purple, line: colors.purpleLine }
      : { soft: colors.accentSoft, ink: colors.accentInk, line: colors.accentLine };

/** One labelled field with an icon tile and optional copy-to-clipboard. */
const DetailField: React.FC<{ icon: string; label: string; value?: string | null; mono?: boolean; copyable?: boolean; flex?: number }> = ({ icon, label, value, mono, copyable, flex }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [hover, setHover] = useState(false);
  const has = !!(value && value.trim());
  const copy = () => {
    if (!has || !isWeb) return;
    const nav = (globalThis as any).navigator;
    nav?.clipboard?.writeText?.(value).then(() => showToast('success', 'Copied', `${label} copied to clipboard`)).catch(() => {});
  };
  return (
    <Pressable
      onPress={copyable && has ? copy : undefined}
      style={{ flex, flexDirection: 'row', alignItems: 'center', gap: 11, padding: 11, borderRadius: 11, borderWidth: 1, borderColor: colors.line, backgroundColor: hover && copyable && has ? colors.paper2 : colors.paper, ...web(copyable && has ? { transition: 'background-color .12s ease', cursor: 'pointer' } : {}) }}
      {...(isWeb && copyable && has ? { onMouseEnter: () => setHover(true), onMouseLeave: () => setHover(false) } : {})}
    >
      <View style={{ width: 32, height: 32, borderRadius: 9, backgroundColor: colors.accentSoft, alignItems: 'center', justifyContent: 'center', ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accentSoft}, ${colors.card})` }) }}>
        <SkIcon name={icon} size={15} color={colors.accentInk} strokeWidth={2} />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ fontFamily: font.sansSemibold, fontSize: 9.5, fontWeight: '600', color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.7 }}>{label}</Text>
        <Text numberOfLines={1} style={{ fontFamily: has ? (mono ? font.monoMedium : font.sansMedium) : font.sans, fontSize: 13.5, fontWeight: '500', color: has ? colors.ink : colors.ink4, marginTop: 2 }}>{has ? value : '—'}</Text>
      </View>
      {copyable && has && isWeb && (
        <View style={{ opacity: hover ? 1 : 0, ...web({ transition: 'opacity .12s ease' }) }}>
          <SkIcon name="link" size={13} color={colors.ink4} strokeWidth={2} />
        </View>
      )}
    </Pressable>
  );
};

const ContactDetailModal: React.FC<{ visible: boolean; contact: Contact | null; stateName: string; canEdit: boolean; onClose: () => void; onEdit: () => void; onDelete: () => void }> = ({ visible, contact, stateName, canEdit, onClose, onEdit, onDelete }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const [data, setData] = useState<Contact | null>(contact);
  const [delHover, setDelHover] = useState(false);
  const [editHover, setEditHover] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setData(contact); // instant paint from the row
    if (contact?.id) {
      let alive = true;
      getContact(contact.id)
        .then((full) => {
          if (!alive || !full) return;
          setData({
            ...contact,
            ...full,
            email: full.email ?? contact.email,
            phone: full.phone ?? contact.phone,
            gstin: full.gstin ?? contact.gstin,
            state_code: full.state_code ?? contact.state_code,
            company: full.company ?? contact.company,
          });
        })
        .catch(() => {});
      return () => { alive = false; };
    }
  }, [visible, contact]);

  const c = data;
  if (!c) return null;
  const [g1, g2] = avatarOf(c.name || '?');
  const tone = TYPE_TONE(c.type, colors);
  const stateLabel = c.state_code ? (stateName ? `${stateName} · ${c.state_code}` : c.state_code) : '';

  const sectionLabel = (t: string) => (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7, marginBottom: 9 }}>
      <View style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: colors.accent }} />
      <Text style={{ fontFamily: font.sansSemibold, fontSize: 10, fontWeight: '600', color: colors.ink3, textTransform: 'uppercase', letterSpacing: 0.8 }}>{t}</Text>
    </View>
  );

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      title="Customer details"
      subtitle="Saved to CRM — the single source of truth"
      width={460}
      headerTone="accent"
      compact
      footer={
        <>
          {canEdit && (
            <Pressable
              onPress={onDelete}
              style={{ height: 38, paddingHorizontal: 14, borderWidth: 1, borderColor: delHover ? colors.dangerLine : colors.line, borderRadius: 10, justifyContent: 'center', flexDirection: 'row', alignItems: 'center', gap: 7, backgroundColor: delHover ? colors.dangerSoft : colors.card, marginRight: 'auto' as any, ...web({ transition: 'all .14s ease', cursor: 'pointer' }) }}
              {...(isWeb ? { onMouseEnter: () => setDelHover(true), onMouseLeave: () => setDelHover(false) } : {})}
            >
              <SkIcon name="trash" size={14} color={delHover ? colors.danger : colors.ink3} strokeWidth={2} />
              <Text style={{ fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', color: delHover ? colors.danger : colors.ink3 }}>Delete</Text>
            </Pressable>
          )}
          <Pressable onPress={onClose} style={{ height: 38, paddingHorizontal: 16, borderWidth: 1, borderColor: colors.line, borderRadius: 10, justifyContent: 'center', backgroundColor: colors.card, ...web({ cursor: 'pointer' }) }}>
            <Text style={{ fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', color: colors.ink2 }}>Close</Text>
          </Pressable>
          {canEdit && (
            <Pressable
              onPress={onEdit}
              style={{ height: 38, paddingHorizontal: 18, borderRadius: 10, justifyContent: 'center', flexDirection: 'row', alignItems: 'center', gap: 7, backgroundColor: colors.accent, borderWidth: 1, borderColor: colors.transparent, ...web({ backgroundImage: `linear-gradient(145deg, ${colors.accent}, ${colors.accentInk})`, boxShadow: editHover ? `0 8px 18px -5px ${colors.accent}aa` : `0 6px 14px -4px ${colors.accent}88`, transform: editHover ? 'translateY(-1px)' : 'translateY(0)', transition: 'all .14s ease', cursor: 'pointer' }) }}
              {...(isWeb ? { onMouseEnter: () => setEditHover(true), onMouseLeave: () => setEditHover(false) } : {})}
            >
              <SkIcon name="edit" size={14} color="#fff" strokeWidth={2.2} />
              <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: '#fff' }}>Edit</Text>
            </Pressable>
          )}
        </>
      }
    >
      <View style={{ gap: 14 }}>
        {/* Hero */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 13 }}>
          <View style={{ width: 52, height: 52, borderRadius: 26, alignItems: 'center', justifyContent: 'center', backgroundColor: g2, ...web({ backgroundImage: `linear-gradient(135deg, ${g1}, ${g2})`, boxShadow: `0 6px 16px -4px ${g2}99` }) }}>
            <Text style={{ fontFamily: font.sansBold, fontSize: 17, fontWeight: '700', color: '#fff' }}>{initials(c.name)}</Text>
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text numberOfLines={1} style={{ fontFamily: font.sansBold, fontSize: 18, fontWeight: '700', color: colors.ink, letterSpacing: -0.3 }}>{c.name}</Text>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <View style={{ paddingHorizontal: 9, paddingVertical: 3, borderRadius: 999, backgroundColor: tone.soft, borderWidth: 1, borderColor: tone.line }}>
                <Text style={{ fontFamily: font.sansSemibold, fontSize: 9.5, fontWeight: '600', letterSpacing: 0.3, color: tone.ink }}>{c.type}</Text>
              </View>
              {!!c.company && <Text numberOfLines={1} style={{ fontFamily: font.sans, fontSize: 12, color: colors.ink4, flex: 1 }}>{c.company}</Text>}
            </View>
          </View>
        </View>

        <View style={{ height: 1, backgroundColor: colors.line }} />

        {/* Contact */}
        <View>
          {sectionLabel('Contact')}
          <View style={{ gap: 9 }}>
            <DetailField icon="send" label="Email address" value={c.email} copyable />
            <DetailField icon="building" label="Phone" value={c.phone} mono copyable />
          </View>
        </View>

        <View style={{ height: 1, backgroundColor: colors.line }} />

        {/* Tax & business */}
        <View>
          {sectionLabel('Tax & business')}
          <View style={{ gap: 9 }}>
            <DetailField icon="percent" label="GSTIN" value={c.gstin} mono copyable />
            <View style={{ flexDirection: 'row', gap: 9 }}>
              <DetailField icon="building" label="State" value={stateLabel} flex={1} />
              <DetailField icon="receipt" label="Company" value={c.company} flex={1} />
            </View>
          </View>
        </View>

        {/* Meta */}
        {(c.created_at || c.updated_at) && (
          <View style={{ flexDirection: 'row', gap: 9, paddingTop: 2 }}>
            {c.created_at && (
              <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 7 }}>
                <SkIcon name="calendar" size={12} color={colors.ink4} strokeWidth={2} />
                <Text style={{ fontFamily: font.sans, fontSize: 11.5, color: colors.ink4 }}>Created {fmtDate(c.created_at)}</Text>
              </View>
            )}
            {c.updated_at && (
              <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 7 }}>
                <SkIcon name="refresh" size={12} color={colors.ink4} strokeWidth={2} />
                <Text style={{ fontFamily: font.sans, fontSize: 11.5, color: colors.ink4 }}>Updated {fmtDate(c.updated_at)}</Text>
              </View>
            )}
          </View>
        )}
      </View>
    </ModalShell>
  );
};

/* ── Create / edit modal ─────────────────────────────────────────── */
const ContactModal: React.FC<{ visible: boolean; editing: Contact | null; onClose: () => void; onSaved: () => void }> = ({ visible, editing, onClose, onSaved }) => {
  const { theme } = useTheme();
  const { colors, font } = theme;
  const IN = COUNTRIES.find((c) => c.code === 'IN')!;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [emailValid, setEmailValid] = useState(true);
  const [phone, setPhone] = useState('');
  const [phoneCountry, setPhoneCountry] = useState<CountryEntry>(IN);
  const [gstin, setGstin] = useState('');
  const [state, setState] = useState('');
  const [company, setCompany] = useState('');
  const [type, setType] = useState<ContactType>('CUSTOMER');
  const [saving, setSaving] = useState(false);

  const fill = useCallback((c: Contact | null) => {
    setName(c?.name || '');
    setEmail(c?.email || '');
    setEmailValid(true);
    // keep only the trailing local digits for the number input
    setPhone((c?.phone || '').replace(/[^\d]/g, '').slice(-10));
    setPhoneCountry(IN);
    setGstin(c?.gstin || '');
    setState(c?.state_code || '');
    setCompany(c?.company || '');
    setType(c?.type || 'CUSTOMER');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!visible) return;
    setSaving(false);
    fill(editing); // instant paint from the row's data
    // The list payload can be partial — refetch the full record so phone,
    // GSTIN, state code and company always show when editing. Merge the full
    // record over the row and keep the row's value wherever the detail
    // response omits a field, so refetching never blanks something the list
    // already had (notably the phone, which the detail endpoint may not return).
    if (editing?.id) {
      let alive = true;
      getContact(editing.id)
        .then((full) => {
          if (!alive || !full) return;
          fill({
            ...editing,
            ...full,
            email: full.email ?? editing.email,
            phone: full.phone ?? editing.phone,
            gstin: full.gstin ?? editing.gstin,
            state_code: full.state_code ?? editing.state_code,
            company: full.company ?? editing.company,
          });
        })
        .catch(() => {});
      return () => { alive = false; };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, editing, fill]);

  const emailOk = !email.trim() || emailValid;
  // GSTIN is optional, but a typed one must be well-formed or CRM rejects it.
  const gstinOk = !gstin.trim() || REGEX.GSTIN.test(gstin.trim().toUpperCase());
  const canSave = !!name.trim() && (!!email.trim() || !!phone.trim()) && emailOk && gstinOk && !saving;

  const save = async () => {
    if (!canSave) return;
    const payload: CreateContactPayload = {
      name: name.trim(),
      ...(email.trim() ? { email: email.trim() } : {}),
      ...(phone.trim() ? { phone: `${phoneCountry.dialCode} ${phone.trim()}` } : {}),
      ...(gstin.trim() ? { gstin: gstin.trim().toUpperCase() } : {}),
      ...(state.trim() ? { state_code: state.trim() } : {}),
      ...(company.trim() ? { company: company.trim() } : {}),
      type,
    };
    setSaving(true);
    try {
      if (editing) {
        // PATCH everything except phone (CRM strips it). If the phone changed,
        // persist it through the dedicated audited route.
        await updateContact(editing.id, payload);
        const origDigits = (editing.phone || '').replace(/[^\d]/g, '').slice(-10);
        const newDigits = phone.trim();
        if (newDigits && newDigits !== origDigits) {
          await changeContactPhone(editing.id, `${phoneCountry.dialCode}${newDigits}`, 'Updated via customer edit');
        }
      } else {
        await createContact(payload);
      }
      onSaved();
    } catch {
      setSaving(false); // toast already shown by the service layer
    }
  };

  const sectionLabel = (t: string) => (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
      <View style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: colors.accent }} />
      <Text style={{ fontFamily: font.sansSemibold, fontSize: 10, fontWeight: '600', color: colors.ink3, textTransform: 'uppercase', letterSpacing: 0.8 }}>{t}</Text>
    </View>
  );

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      title={editing ? 'Edit customer' : 'New customer'}
      subtitle="Saved to CRM — the single source of truth"
      width={440}
      headerTone="accent"
      compact
      footer={
        <>
          <Pressable onPress={onClose} style={{ height: 38, paddingHorizontal: 16, borderWidth: 1, borderColor: colors.line, borderRadius: 10, justifyContent: 'center', backgroundColor: colors.card, ...web({ cursor: 'pointer' }) }}>
            <Text style={{ fontFamily: font.sansMedium, fontSize: 13, fontWeight: '500', color: colors.ink2 }}>Cancel</Text>
          </Pressable>
          <Pressable
            onPress={save}
            disabled={!canSave}
            style={{
              height: 38, paddingHorizontal: 18, borderRadius: 10, justifyContent: 'center', flexDirection: 'row', alignItems: 'center', gap: 7,
              backgroundColor: canSave ? colors.accent : colors.paper3, borderWidth: 1, borderColor: canSave ? colors.transparent : colors.line,
              ...(canSave ? web({ backgroundImage: `linear-gradient(145deg, ${colors.accent}, ${colors.accentInk})`, boxShadow: `0 6px 14px -4px ${colors.accent}88`, cursor: 'pointer' }) : web({ cursor: 'not-allowed' })),
            }}
          >
            <SkIcon name="check" size={15} color={canSave ? '#fff' : colors.ink4} strokeWidth={2.4} />
            <Text style={{ fontFamily: font.sansSemibold, fontSize: 13, fontWeight: '600', color: canSave ? '#fff' : colors.ink4 }}>{saving ? 'Saving…' : editing ? 'Save changes' : 'Create customer'}</Text>
          </Pressable>
        </>
      }
    >
      <View style={{ gap: 12 }}>
        {/* Contact */}
        <View style={{ gap: 10 }}>
          {sectionLabel('Contact')}
          <CommonInput label="Name *" value={name} onChangeText={setName} placeholder="e.g. Acme Pvt Ltd" leftIcon={<SkIcon name="building" size={16} color={colors.ink4} strokeWidth={2} />} containerStyle={{ marginBottom: 0 }} />
          <EmailComponent key={editing?.id || 'new'} defaultValue={editing?.email || ''} onValueChange={(e, valid) => { setEmail(e); setEmailValid(valid); }} containerStyle={{ marginVertical: 0 }} />
          <MobileNumberInput label="Phone" value={phone} selectedCountry={phoneCountry} onCountryChange={setPhoneCountry} onChangeText={setPhone} placeholder="00000 00000" />
          <Text style={{ fontFamily: font.sans, fontSize: 11, lineHeight: 15, color: colors.ink4, marginTop: -2 }}>Provide at least an email or a phone number.{editing ? ' Phone changes are recorded in the CRM audit log.' : ''}</Text>
        </View>

        <View style={{ height: 1, backgroundColor: colors.line }} />

        {/* Tax & business */}
        <View style={{ gap: 10 }}>
          {sectionLabel('Tax & business')}
          <View style={{ flexDirection: 'row', gap: 12 }}>
            <View style={{ flex: 2 }}>
              <CommonInput label="GSTIN" value={gstin} onChangeText={(t) => setGstin(t.toUpperCase().slice(0, 15))} error={gstinOk ? undefined : 'Enter a valid 15-character GSTIN'} placeholder="27ABCDE1234F1Z5" leftIcon={<SkIcon name="percent" size={15} color={colors.ink4} strokeWidth={2} />} inputProps={{ autoCapitalize: 'characters' }} containerStyle={{ marginBottom: 0 }} />
            </View>
            <View style={{ flex: 1 }}>
              <CommonInput label="State code" value={state} onChangeText={(t) => setState(t.replace(/\D/g, '').slice(0, 2))} placeholder="27" inputProps={{ keyboardType: 'number-pad' }} containerStyle={{ marginBottom: 0 }} />
            </View>
          </View>
          <CommonInput label="Company" value={company} onChangeText={setCompany} placeholder="Legal / company name" containerStyle={{ marginBottom: 0 }} />
          <View>
            <Text style={{ fontFamily: font.sansMedium, fontSize: 12, color: colors.ink3, marginBottom: 6, fontWeight: '500' }}>Type</Text>
            <DropdownSelect value={type} options={TYPES.map((t) => [t, t])} onChange={(v) => setType(v as ContactType)} />
          </View>
        </View>
      </View>
    </ModalShell>
  );
};

export default Contacts;
